import React from 'react';
import { Alert, Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  CopyField, ListTable, Modal, ScrollArea, SelectMenu, SettingRow, SettingSwitch, StatusChip, TokenField,
  ToolbarButton, formatRelativeTime, type ListColumn,
} from '../../ui-kit';
import { ConfirmDialog } from '../../components/dialog';
import {
  addShareViewers, changeShare, ensureShare, removeShareViewer, revokeShare,
  type Share, type ShareViewerRow,
} from '../../services/sharingService';

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const normaliseEmail = (raw: string) => (EMAIL.test(raw.trim()) ? raw.trim().toLowerCase() : null);

const NEVER = 'never';
/** The select's value while the link ends on a date: the date shows, and no option is ticked. */
const CURRENT = 'current';
/** About five people; more scroll inside the list. */
const PEOPLE_HEIGHT = 300;
const LASTS = [
  ...[7, 14, 30, 60, 90].map((d) => ({ value: String(d), label: `${d} days from today` })),
  { value: NEVER, label: 'Never', description: 'Works until you turn the link off' },
];

function untilDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** "Not yet", "Once", "Twice", "7 times". */
export function openedWords(n: number): string {
  return n <= 0 ? 'Not yet' : n === 1 ? 'Once' : n === 2 ? 'Twice' : `${n} times`;
}

/**
 * Share one thing by link. The link is there as the window opens: copy it and
 * pass it on yourself, or invite people by email, which sends it for you.
 * Either way each person verifies their email with a code before they see
 * anything, and shows under People, where they can be removed. Admins only;
 * the server checks that too.
 */
export default function ShareDialog({
  open, onClose, kind, targetId, targetName, options,
}: {
  open: boolean;
  onClose: () => void;
  kind: string;
  targetId: string;
  targetName: string;
  /** Kind-specific, e.g. { view: 'campaigns' } for one page of a board. */
  options?: Record<string, unknown>;
}) {
  const [share, setShare] = React.useState<Share | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [confirmOff, setConfirmOff] = React.useState(false);
  const [invites, setInvites] = React.useState<string[]>([]);
  // What is typed and not yet a chip. Send invite takes it as it stands, so
  // nobody has to know to press Enter first.
  const [typed, setTyped] = React.useState('');
  const [sentTo, setSentTo] = React.useState<string[]>([]);
  // A fresh box after each send, so no text is left behind in it.
  const [inviteRound, setInviteRound] = React.useState(0);

  const optionsKey = JSON.stringify(options ?? {});

  // The link that already opens this, or a new one: made as the window opens,
  // so there is always a link to copy.
  const load = React.useCallback(() => {
    setLoading(true);
    setProblem(null);
    return ensureShare(kind, targetId, JSON.parse(optionsKey))
      .then(setShare)
      .catch((err: Error) => setProblem(err.message))
      .finally(() => setLoading(false));
  }, [kind, targetId, optionsKey]);

  React.useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const run = async (what: string, action: () => Promise<Share>): Promise<boolean> => {
    setBusy(what);
    setProblem(null);
    try {
      setShare(await action());
      return true;
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Something went wrong');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const typedEmails = typed.split(/[\s,;]+/).map(normaliseEmail).filter((e): e is string => e !== null);
  const toInvite = Array.from(new Set([...invites, ...typedEmails]));

  const invite = (emails: string[]) => {
    if (!share || !emails.length || busy !== null) return;
    setSentTo([]);
    void run('invite', () => addShareViewers(share.share_id, emails)).then((ok) => {
      if (!ok) return;
      setSentTo(emails);
      setInvites([]);
      setTyped('');
      setInviteRound((n) => n + 1);
    });
  };

  const off = share !== null && share.state !== 'live';
  const viewers = (share?.viewers ?? []).filter((v) => !v.removed);
  const neverExpires = Boolean(share && (share.never_expires || !share.expires_at));

  const columns: ListColumn<ShareViewerRow>[] = [
    {
      key: 'person', header: 'Person', grow: 1, minWidth: 200,
      render: (v) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {v.name || v.email}
          </Typography>
          {v.name ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
              {v.email}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      // Invited by email here, or came in with the link someone passed on.
      key: 'how', header: 'How', width: 130,
      render: (v) => <StatusChip tone="neutral" plain label={v.invited ? 'Invited' : 'Used the link'} />,
    },
    {
      // How often, and when last: all the history the window needs. The
      // full record of codes, sign-ins and downloads stays in the audit.
      key: 'opened', header: 'Opened', width: 130,
      render: (v) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: v.times_opened ? t.tokens.text.primary : t.tokens.text.tertiary })}>
            {openedWords(v.times_opened)}
          </Typography>
          {v.times_opened && v.last_seen_at ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              last {formatRelativeTime(v.last_seen_at)}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: 80, align: 'right',
      render: (v) => (
        <ToolbarButton tone="danger" icon={<DeleteOutlineRounded fontSize="small" />}
          aria-label={`Remove ${v.name || v.email}`} tooltip="Remove: they lose access on their next click"
          disabled={busy !== null} loading={busy === `remove:${v.viewer_id}`}
          onClick={() => { if (share) void run(`remove:${v.viewer_id}`, () => removeShareViewer(share.share_id, v.viewer_id)); }} />
      ),
    },
  ];

  const body = loading && !share ? (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={44} />
      <Skeleton variant="rounded" height={44} />
      <Skeleton variant="rounded" height={120} />
    </Stack>
  ) : !share ? null : off ? (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
        This link is turned off, so it no longer opens for anyone. Make a new one to share {targetName} again.
      </Typography>
      <ToolbarButton variant="brand" loading={loading} onClick={() => { void load(); }}>Make a new link</ToolbarButton>
    </Stack>
  ) : (
    <Stack spacing={3}>
      {/* Getting it to people: the link to pass on, or emails to send it to. */}
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Share the link</Typography>
          <CopyField value={share.link} ariaLabel={`Link to ${targetName}`} />
          <Typography variant="caption" sx={(t) => ({ display: 'block', mt: 0.75, color: t.tokens.text.secondary })}>
            Anyone who opens the link verifies their email with a code before they see anything.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TokenField key={inviteRound} id="share-invite" ariaLabel="Emails to send the link to"
              value={invites} onChange={(next) => { setInvites(next); setSentTo([]); }}
              onDraftChange={(text) => { setTyped(text); if (text) setSentTo([]); }} onSubmit={invite}
              addWhenValid normalise={normaliseEmail} invalidText="That does not look like an email address"
              placeholder="Or type an email, and we send them the link" />
          </Box>
          <ToolbarButton variant="solid" size="medium" loading={busy === 'invite'}
            disabled={!toInvite.length || busy !== null} onClick={() => invite(toInvite)}>
            Send invite
          </ToolbarButton>
        </Stack>
        {sentTo.length ? (
          <Typography variant="caption" role="status" sx={(t) => ({ color: t.tokens.semantic.success })}>
            Invite sent to {sentTo.join(', ')}.
          </Typography>
        ) : null}
      </Stack>

      {/* The link's settings, each a row that saves as it is changed, so
          the window needs no Done or Save. */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1.5 }}>Link settings</Typography>
        <Stack spacing={2} divider={<Divider />}>
        <SettingSwitch
          id="share-allow-export"
          label="Allow downloads"
          description={share.allow_export
            ? 'Viewers can download tables and charts as files. Each download is recorded.'
            : 'Viewers can look, but not download anything.'}
          checked={share.allow_export}
          busy={busy === 'export'}
          disabled={busy !== null && busy !== 'export'}
          onChange={(next) => { void run('export', () => changeShare(share.share_id, { allow_export: next })); }}
        />
        <SettingRow
          label="Link expires"
          description={neverExpires
            ? 'The link works until you turn it off.'
            : 'After this date the link stops opening for everyone.'}
        >
          <SelectMenu
            label="Expires"
            value={neverExpires ? NEVER : CURRENT}
            valueLabel={share.expires_at ? untilDate(share.expires_at) : undefined}
            options={LASTS}
            disabled={busy !== null}
            onChange={(next) => {
              void run('days', () => changeShare(share.share_id, next === NEVER ? { never: true } : { days: Number(next) }));
            }}
          />
        </SettingRow>
        <SettingRow
          label="Turn off link"
          description="The link stops opening, and anyone viewing is cut off on their next click."
        >
          <ToolbarButton tone="danger" disabled={busy !== null} onClick={() => setConfirmOff(true)}>
            Turn off
          </ToolbarButton>
        </SettingRow>
        </Stack>
      </Box>

      {/* Who has it. The list scrolls inside itself, so a long one never
          stretches the window. */}
      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>People ({viewers.length})</Typography>
        <ScrollArea maxHeight={PEOPLE_HEIGHT} ariaLabel="People this is shared with">
          <ListTable columns={columns} rows={viewers} getRowKey={(v) => v.viewer_id}
            emptyMessage="Nobody yet. People you invite, and anyone who opens the link and verifies their email, show here." />
        </ScrollArea>
      </Box>
    </Stack>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        busy={busy !== null}
        size="md"
        title={`Share ${targetName}`}
        subtitle="A read-only link that always shows the latest numbers."
      >
        <Stack spacing={2}>
          {problem ? <Alert severity="error">{problem}</Alert> : null}
          {body}
        </Stack>
      </Modal>
      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        title="Turn off this link?"
        description="Anyone viewing it is cut off on their next click, and the link stops opening. You can make a new one afterwards."
        tone="destructive"
        confirmLabel="Turn off"
        busy={busy === 'revoke'}
        onConfirm={async () => {
          if (share) await run('revoke', () => revokeShare(share.share_id));
          setConfirmOff(false);
        }}
      />
    </>
  );
}
